import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import Footer from '../../components/layout/footer';
import { FormattedMessage } from 'react-intl';
import dlt from '../../utils/default-lang';
import PersonalInformationProcessingForm from '../../components/personal-information-processing-form';
import SmsVerification from '../../components/sms-verification';
import { BodyText } from '../../components/typography';
import { ReactComponent as CheckCircle } from '../../assets/img/svg/check-circle.svg';
import { ReactComponent as XCircle } from '../../assets/img/svg/x-circle.svg';
import InlineLoading from '../../components/ui/inline-loading';
import WishmakerModal from '../../components/ui/modal/modal';
import * as ActionCreators from '../../redux/action';
import { CardSC, CardTopSC, ContainerSC } from './personal-information-processing.styled';
import Button from '../../components/ui/button';

const PersonalInformationProcessing = ({
  match,
  registrationCheck,
  sendPublicSms,
  sendPersonalInformationProcessingAgrees,
  history,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [registrationData, setRegistrationData] = useState();
  const [additionalModalData, setAdditionalModalData] = useState({});
  const [isSmsModalOpened, setIsSmsModalOpened] = useState(false);
  const [isSmsCodeError, setIsSmsCodeError] = useState(false);
  const [isSmsModalError, setIsSmsModalError] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const wmNumber = match.params.wmNumber;

  const fetchRegistrationStatus = useCallback(() => {
    registrationCheck({
      id: wmNumber,
      onSuccess: (response) => {
        setRegistrationData(response);
        setIsLoading(false);
      },
    });
  }, [registrationCheck, wmNumber]);

  const registrationStatus = useMemo(() => {
    if (!registrationData) return;
    if (registrationData.message === 'NOT_FOUND') {
      return 'NOT_FOUND';
    }
    if (registrationData.message === 'REGISTRATION_NOT_COMPLETED') {
      if (registrationData.data.contact_status === 11) {
        return 'AGREES_SIGNED';
      } else {
        return 'REGISTRATION_NOT_COMPLETED';
      }
    }
    if (registrationData.message === 'REGISTERED') {
      return 'REGISTERED';
    }
  }, [registrationData]);

  useEffect(() => {
    fetchRegistrationStatus();
  }, [fetchRegistrationStatus]);

  const onSmsSend = () => {
    sendPublicSms({ type: 'personal-information-processing', wmNumber });
  };

  const onFormSubmit = async (formData) => {
    setAdditionalModalData(formData);
    onSmsSend();
    setIsSmsModalOpened(true);
  };

  const onSmsModalSubmit = (smsModalData) => {
    const formattedData = new FormData();

    formattedData.append('sms_code', smsModalData.sms_code);

    formattedData.append('information', additionalModalData.information);
    formattedData.append('marketing', additionalModalData.marketing);
    formattedData.append('rejected', additionalModalData.rejected);
    formattedData.append('termination', additionalModalData.termination);

    formattedData.append('type_doc_1', additionalModalData.first_identity);
    formattedData.append('document_number_1', additionalModalData.first_identity_number);
    formattedData.append('type_doc_2', additionalModalData.second_identity);
    formattedData.append('document_number_2', additionalModalData.second_identity_number);

    if (additionalModalData.first_identity_image_front !== null) {
      formattedData.append('identity_doc_1_front', additionalModalData.first_identity_image_front);
    }
    if (additionalModalData.first_identity_image_back !== null) {
      formattedData.append('identity_doc_1_back', additionalModalData.first_identity_image_back);
    }

    if (additionalModalData.second_identity_image_front !== null) {
      formattedData.append('identity_doc_2_front', additionalModalData.second_identity_image_front);
    }
    if (additionalModalData.second_identity_image_back !== null) {
      formattedData.append('identity_doc_2_back', additionalModalData.second_identity_image_back);
    }

    const onBadSmsCode = () => {
      setIsSending(false);
      setIsSmsCodeError(true);
    };
    const onSuccess = () => {
      setIsSending(false);
      setIsSmsModalOpened(false);
      setAdditionalModalData({});
      setIsLoading(true);
      fetchRegistrationStatus();
    };
    const onError = () => {
      setIsSmsModalError(true);
      setIsSending(false);
    };

    const sendAgreesData = async () => {
      await sendPersonalInformationProcessingAgrees({
        data: formattedData,
        wmNumber,
        onSuccess,
        onBadSmsCode,
        onError,
      });
      setIsSending(true);
    };

    sendAgreesData();
  };

  return (
    <>
      <ContainerSC>
        <CardSC>
          <CardTopSC>
            <BodyText className="bolder">
              <FormattedMessage
                id="personal_information_title"
                defaultMessage={dlt['personal_information_title']}
              />
            </BodyText>
            <BodyText className="black-transparent-lighter bolder">
              <FormattedMessage
                id="personal_information_subtitle"
                defaultMessage={dlt['personal_information_subtitle']}
                values={{ wmNumber }}
              />
            </BodyText>
          </CardTopSC>
          {isLoading ? (
            <div className="loading-wrap">
              <InlineLoading />
            </div>
          ) : registrationStatus === 'NOT_FOUND' ? (
            <div className="success">
              <div className="icon-wrapper">
                <XCircle />
              </div>
              <div className="texts-wrapper">
                <BodyText className="bolder">
                  <FormattedMessage
                    id="personal_information_processing_id_not_found"
                    defaultMessage={dlt['personal_information_processing_id_not_found']}
                  />
                </BodyText>
                <Button type="PRIMARY" className="redirect-btn" onClick={() => history.push('/')}>
                  <FormattedMessage
                    id="personal_information_processing_return_to_login"
                    defaultMessage={dlt['personal_information_processing_return_to_login']}
                  />
                </Button>
              </div>
            </div>
          ) : registrationStatus === 'REGISTERED' || registrationStatus === 'AGREES_SIGNED' ? (
            <div className="success">
              <div className="icon-wrapper">
                <CheckCircle className="check-circle" />
              </div>
              <div className="texts-wrapper">
                <BodyText className="bolder">
                  <FormattedMessage
                    id="personal_information_processing_thanks"
                    defaultMessage={dlt['personal_information_processing_thanks']}
                  />
                </BodyText>
                <BodyText className="bolder">
                  <FormattedMessage
                    id="personal_information_processing_registration_completed"
                    defaultMessage={dlt['personal_information_processing_registration_completed']}
                  />
                </BodyText>
                {registrationStatus === 'REGISTERED' ? (
                  <>
                    <BodyText className="bolder last-paragraph">
                      <FormattedMessage
                        id="personal_information_processing_login_now"
                        defaultMessage={dlt['personal_information_processing_login_now']}
                      />
                    </BodyText>
                    <Button
                      type="PRIMARY"
                      className="redirect-btn"
                      onClick={() => history.push('/')}
                    >
                      <FormattedMessage
                        id="personal_information_processing_continue_to_login"
                        defaultMessage={dlt['personal_information_processing_continue_to_login']}
                      />
                    </Button>
                  </>
                ) : (
                  <BodyText className="bolder last-paragraph">
                    <FormattedMessage
                      id="personal_information_processing_we_will_contact_you"
                      defaultMessage={dlt['personal_information_processing_we_will_contact_you']}
                    />
                  </BodyText>
                )}
              </div>
            </div>
          ) : (
            <PersonalInformationProcessingForm
              onSubmit={onFormSubmit}
              registrationData={registrationData}
            />
          )}
        </CardSC>
      </ContainerSC>
      <Footer />
      <WishmakerModal
        isOpened={isSmsModalOpened}
        onClose={() => {
          setIsSmsModalOpened(false);
          setIsSending(false);
        }}
        className="special"
        withCross
      >
        <SmsVerification
          onSubmit={(data) => onSmsModalSubmit(data)}
          additionalData={additionalModalData}
          additionalInfo="personal_information_processing_sms_modal_additional_info"
          isError={isSmsCodeError}
          errorMessage={isSmsModalError ? 'personal_information_processing_sms_modal_error' : ''}
          isSending={isSending}
          onResend={onSmsSend}
          onBadSms={() => setIsSmsCodeError(false)}
        />
      </WishmakerModal>
    </>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      registrationCheck: ActionCreators.registrationCheck,
      sendPublicSms: ActionCreators.sendPublicSms,
      sendPersonalInformationProcessingAgrees:
        ActionCreators.sendPersonalInformationProcessingAgrees,
    },
    dispatch
  );
};

export default withRouter(connect(null, mapDispatchToProps)(PersonalInformationProcessing));
